import { useEffect, useState } from "react";
import { History } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../api/axios";
import DataTable from "./DataTable";
import RowIconAction from "./RowIconAction";
import ShopOrderHistoryDialog from "./ShopOrderHistoryDialog";

const formatMoney = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const titleCase = (value = "") => value.charAt(0).toUpperCase() + value.slice(1);
const tooltipStyle = { border: "1px solid #d9dee3", borderRadius: 4, background: "#fff", fontSize: 12 };

export default function DistrictNetworkDetail({ districtId, range, source = "dashboard" }) {
  const [detail, setDetail] = useState(null);
  const [shopPage, setShopPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [historyShop, setHistoryShop] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const endpoint = source === "network" ? `/network/districts/${districtId}/overview` : `/dashboard/districts/${districtId}`;
    api.get(endpoint, { params: { range, page: shopPage, limit: 25 } })
      .then(({ data }) => { if (active) setDetail(data); })
      .catch((requestError) => {
        if (active) setError(requestError.response?.data?.message || "Could not load district network details.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [districtId, range, shopPage, source]);

  const openHistory = (shop) => {
    setHistoryShop(shop);
  };

  return <section className="district-network-detail" aria-label="Selected district network details">
    {loading && !detail && <div className="dashboard-loading" role="status">Loading district relationships…</div>}
    {error && <div className="dashboard-error" role="alert">{error}</div>}
    {detail && <>
      <div className="district-detail-heading">
        <div><span>{detail.district.state}</span><strong>{detail.district.name}</strong></div>
        <small>{loading ? "Updating…" : `${detail.range.label} · current assignments`}</small>
      </div>

      <div className="district-network-stats">
        <DistrictStat label="Distributors" value={detail.counts.distributors} />
        <DistrictStat label="Sales people" value={detail.counts.salesPeople} />
        <DistrictStat label="Shops" value={detail.counts.shops} />
        <DistrictStat label="Orders in period" value={detail.counts.orders} />
        <DistrictStat label="Order value" value={formatMoney(detail.orderValue?.total ?? 0)} />
      </div>

      <section className="district-detail-panel">
        <div className="dashboard-panel-heading"><div><h2>District order flow</h2><p>Distributor purchases and shop orders · {detail.range.label.toLowerCase()}</p></div></div>
        <div className="district-order-chart">
          {detail.trend?.some((item) => item.distributorValue || item.shopValue) ? <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={detail.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="districtDirectFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#246b8e" stopOpacity={0.2} /><stop offset="100%" stopColor="#246b8e" stopOpacity={0.01} /></linearGradient>
                <linearGradient id="districtShopFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2a9d8f" stopOpacity={0.18} /><stop offset="100%" stopColor="#2a9d8f" stopOpacity={0.01} /></linearGradient>
              </defs>
              <CartesianGrid stroke="#e9edf0" vertical={false} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: "#71808b", fontSize: 10 }} minTickGap={18} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: "#71808b", fontSize: 10 }} tickFormatter={(value) => new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(value)} width={44} />
              <Tooltip formatter={(value, name) => [formatMoney(value), name === "distributorValue" ? "Distributor purchases" : "Shop orders"]} contentStyle={tooltipStyle} />
              <Legend formatter={(name) => name === "distributorValue" ? "Distributor purchases" : "Shop orders"} />
              <Area type="monotone" dataKey="distributorValue" name="distributorValue" stroke="#246b8e" strokeWidth={2} fill="url(#districtDirectFill)" />
              <Area type="monotone" dataKey="shopValue" name="shopValue" stroke="#2a9d8f" strokeWidth={2} fill="url(#districtShopFill)" />
            </AreaChart>
          </ResponsiveContainer> : <div className="district-chart-empty">No order activity in this district for {detail.range.label.toLowerCase()}.</div>}
        </div>
      </section>

      <section className="district-detail-panel">
        <div className="dashboard-panel-heading"><div><h2>Distributor network</h2><p>Each distributor's assigned sales people, shops, and order contribution.</p></div></div>
        {detail.distributors.length ? <DataTable headers={["Distributor", "Status", "Sales people", "Shops", "Direct orders", "Shop orders"]} wrapStyle={{ border: 0, borderRadius: 0 }} tableStyle={{ minWidth: 0 }}>
          {detail.distributors.map((item) => <tr key={item.id}>
            <td><strong>{item.name}</strong><small>{item.contact}{item.email ? ` · ${item.email}` : ""}</small></td>
            <td><span className={`dashboard-order-status status-${item.status === "blocked" ? "cancelled" : item.status}`}>{titleCase(item.status)}</span></td>
            <td>{item.salesPeople}</td>
            <td>{item.shops}</td>
            <td>{item.directOrders} · {formatMoney(item.directValue)}</td>
            <td>{item.shopOrders} · {formatMoney(item.shopValue)}</td>
          </tr>)}
        </DataTable> : <div className="district-empty">No distributors are assigned to this district.</div>}
      </section>

      <section className="district-detail-panel">
        <div className="dashboard-panel-heading"><div><h2>Assigned sales people</h2><p>Mapped to their distributor and shop-order activity in this period.</p></div></div>
        {detail.salesPeople.length ? <DataTable headers={["Sales person", "Distributor", "Shops", "Orders", "Order value"]} wrapStyle={{ border: 0, borderRadius: 0 }} tableStyle={{ minWidth: 0 }}>
          {detail.salesPeople.map((person) => <tr key={person.id}>
            <td><strong>{person.name}</strong><small>{person.email}</small></td>
            <td>{person.distributor}</td>
            <td>{person.shops}</td>
            <td>{person.orders}</td>
            <td>{formatMoney(person.orderValue)}</td>
          </tr>)}
        </DataTable> : <div className="district-empty">No sales people are assigned to this district's distributors.</div>}
      </section>

      <section className="district-detail-panel">
        <div className="dashboard-panel-heading"><div><h2>Shops in district</h2><p>Shop → salesperson → distributor · order history follows the active date range.</p></div><span className="district-list-count">{detail.shopPagination?.total ?? 0} shops</span></div>
        {detail.shops.length ? <DataTable headers={["Shop / owner", "Location", "Distributor", "Sales person", "Orders", "History"]} wrapStyle={{ border: 0, borderRadius: 0 }} tableStyle={{ minWidth: 0 }}>
          {detail.shops.map((shop) => <tr key={shop.id}>
            <td><strong>{shop.name}</strong><small>{shop.owner}{shop.phone ? ` · ${shop.phone}` : ""}</small></td>
            <td>{shop.city || "-"}</td>
            <td>{shop.distributor}</td>
            <td>{shop.salesperson}</td>
            <td>{shop.orders} · {formatMoney(shop.orderValue)}</td>
            <td><div className="row-actions"><RowIconAction label={`View ${shop.name} order history`} icon={History} onClick={() => openHistory(shop)} /></div></td>
          </tr>)}
        </DataTable> : <div className="district-empty">No shops are listed under distributors in this district.</div>}
        {(detail.shopPagination?.pages || 1) > 1 && <div className="district-pagination"><span>Page {detail.shopPagination.page} of {detail.shopPagination.pages}</span><button type="button" disabled={shopPage <= 1 || loading} onClick={() => setShopPage((current) => current - 1)}>Previous</button><button type="button" disabled={shopPage >= detail.shopPagination.pages || loading} onClick={() => setShopPage((current) => current + 1)}>Next</button></div>}
      </section>
    </>}

    {historyShop && <ShopOrderHistoryDialog
      shop={historyShop}
      range={range}
      endpoint={source === "network" ? `/network/shops/${historyShop.id}/orders` : `/dashboard/districts/${districtId}/shops/${historyShop.id}/orders`}
      onClose={() => setHistoryShop(null)}
    />}
  </section>;
}

function DistrictStat({ label, value }) {
  return <div className="district-network-stat"><span>{label}</span><strong>{value}</strong></div>;
}